import { forwardRef, type ReactNode } from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { type DataTestId } from '../../core/types/data-props.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { Flex } from '../../layouts/flex/Flex.js';

//#region Typings
/**
 * Accepted properties for the Button Prefix.
 * @public
 */
export interface ButtonPrefixProps
  extends StylingProps,
    DataTestId,
    BehaviorTrackingProps {
  /** Elements to be displayed in the Button Prefix slot. */
  children: ReactNode;
}
//#endregion

/**
 * Button Prefix component.
 * @public
 */
export const Prefix = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  ButtonPrefixProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: ButtonPrefixProps = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);

  return (
    <Flex
      data-testid={dataTestId}
      ref={forwardedRef}
      gap={0}
      className={consumerClassName}
      style={consumerStyle}
      {...behavioralTrackingProps}
    >
      {children}
    </Flex>
  );
});

(Prefix as typeof Prefix & { displayName: string }).displayName =
  'Button.Prefix';
